import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import { Map, List, fromJS } from 'immutable'

import ContextSubscriber from '@aytm/lib/common/components/decorators/ContextSubscriber'
import ManagedRenderComponent from '@aytm/lib/common/components/ManagedRenderComponent'

import { updateItemModel } from '@aytm/app/stats/page/sidebar/reports/actions'

import {
  isSkipperReport,
  isSkipperGuidedUnsettledReport
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'

// components
import { Checkbox } from 'primereact/checkbox'

import ReportPopupSection from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupSection'
import ReportPopupAppliedFilters from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupAppliedFilters'
import { ReportPopupSkipperSettings } from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupAnalysisSettings'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportPopup.module.scss'

// Accordion section keys for the description column.
const SECTION_FILTERS = 'filters'
const SECTION_ANALYSIS = 'analysis'

// <Component>
// eslint-disable-next-line no-class-assign
class ReportPopupDescription extends ManagedRenderComponent {
  static propTypes = {}
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.state != prevProps.state ||
      nextProps.model != prevProps.model ||
      nextProps.position != prevProps.position
    )
  }

  constructor(props, context) {
    super(props, context)

    // a Skipper Report is opened to read its settings; every other report has only the filters
    this.state = { openSection: isSkipperReport(props.model) ? SECTION_ANALYSIS : SECTION_FILTERS }

    this.getModelProps = this.getModelProps.bind(this)
    this.onHiddenChange = this.onHiddenChange.bind(this)
    this.onProtectedChange = this.onProtectedChange.bind(this)
    this.getDateApproximation = this.getDateApproximation.bind(this)
    this.toggleSection = this.toggleSection.bind(this)

    this.timestepInterval = null
  }

  // Accordion toggle — opening a section closes any other; clicking the open one closes it.
  toggleSection(key) {
    this.setState(prevState => ({ openSection: prevState.openSection === key ? null : key }))
  }

  getModelProps() {
    const { url, state, model, listState, listModel, actions } = this.props

    return {
      url,
      listState,
      listModel,
      state,
      model,
      actions
    }
  }

  onHiddenChange() {
    const { model } = this.props

    updateItemModel({
      ...this.getModelProps(),
      data: {
        do: 'flags',
        id: model.get('id'),
        flags: { hidden: !model.get('hidden'), protected: model.get('protected') }
      },
      updates: [{ path: ['hidden'], value: !model.get('hidden') }]
    })
  }

  onProtectedChange() {
    const { model } = this.props

    updateItemModel({
      ...this.getModelProps(),
      data: {
        do: 'flags',
        id: model.get('id'),
        flags: { hidden: model.get('hidden'), protected: !model.get('protected') }
      },
      updates: [{ path: ['protected'], value: !model.get('protected') }]
    })
  }

  getDateApproximation(timestep) {
    const localDate = moment(1000 * timestep).utcOffset(
      this.props.timeZone.has('offset') ? this.props.timeZone.get('offset') : moment().utcOffset()
    )

    const minutesDiff = Math.floor(moment().diff(localDate, 'minutes'))
    const hoursDiff = Math.floor(moment().diff(localDate, 'hours'))
    const daysDiff = Math.floor(moment().diff(localDate, 'days'))
    const monthsDiff = Math.floor(moment().diff(localDate, 'months'))
    const yearsDiff = Math.floor(moment().diff(localDate, 'years'))

    if (minutesDiff < 1) {
      return `just now`
    }
    if (hoursDiff < 1) {
      return `${minutesDiff} ${1 === minutesDiff ? 'minute' : 'minutes'} ago`
    }
    if (daysDiff < 1) {
      return `${hoursDiff} ${1 === hoursDiff ? 'hour' : 'hours'} ago`
    }
    if (monthsDiff < 1) {
      return `${daysDiff} ${1 === daysDiff ? 'day' : 'days'} ago`
    }
    if (yearsDiff < 1) {
      return `${monthsDiff} ${1 === monthsDiff ? 'month' : 'months'} ago`
    }
    return `${yearsDiff} ${1 === yearsDiff ? 'year' : 'years'} ago`
  }

  componentDidMount() {
    this.timestepInterval = setInterval(() => {
      if (this.refCreatedTimestep) {
        this.refCreatedTimestep.innerHTML = this.getDateApproximation(this.props.model.get('created'))
      }
      if (this.refEditedTimestep) {
        this.refEditedTimestep.innerHTML = this.getDateApproximation(this.props.model.get('edited'))
      }
    }, 60000)
  }

  componentWillUnmount() {
    clearInterval(this.timestepInterval)
  }

  render() {
    const {
      model,
      position,
      features,
      virtualReportsIds,
      isAdmin,
      isSurveyGuest,
      isSurveyViewer,
      isSurveyCompleted,
      getReportFilters
    } = this.props
    const { openSection } = this.state
    const isDefault = 1 == position
    const isLocked =
      ['original', 'widget', ...virtualReportsIds].includes(model.get('type')) || isSkipperGuidedUnsettledReport(model)
    // computed per report when the popup opens, instead of eagerly for every report
    const filters = getReportFilters ? fromJS(getReportFilters(model.get('id'))) : List()
    const creator = model.get('creator')
    const editor = model.get('editor')

    return (
      <div className={style.description}>
        <div className={style['description-head']}>
          {!['original', 'xpert', 'widget', 'skipper', 'concept', ...virtualReportsIds].includes(model.get('type')) ? (
            <>
              {'number' === typeof model.get('created') ? (
                <div>
                  Created{' '}
                  {!isSurveyGuest &&
                    !isSurveyViewer &&
                    'string' == typeof creator &&
                    0 < creator.length &&
                    `by ${creator} `}
                  <b ref={this.saveRef('CreatedTimestep')}>{this.getDateApproximation(model.get('created'))}</b>
                </div>
              ) : null}
              {'number' === typeof model.get('edited') ? (
                <div>
                  Modified{' '}
                  {!isSurveyGuest &&
                    !isSurveyViewer &&
                    'string' == typeof editor &&
                    0 < editor.length &&
                    `by ${editor} `}
                  <b ref={this.saveRef('EditedTimestep')}>{this.getDateApproximation(model.get('edited'))}</b>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <div className={style['description-content']}>
          <ReportPopupSection
            title="Applied filters & customizations"
            open={SECTION_FILTERS === openSection}
            onToggle={() => this.toggleSection(SECTION_FILTERS)}
          >
            <ReportPopupAppliedFilters filters={filters} />
          </ReportPopupSection>
          <ReportPopupSkipperSettings
            report={model}
            features={features}
            open={SECTION_ANALYSIS === openSection}
            isSurveyCompleted={isSurveyCompleted}
            onToggle={() => this.toggleSection(SECTION_ANALYSIS)}
          />
        </div>
        {isAdmin && !isLocked ? (
          <div className={style['description-footer']}>
            {isDefault ? null : (
              <>
                <Checkbox
                  className="p-checkbox-sm"
                  value="Invisible to client"
                  checked={model.get('hidden')}
                  onChange={this.onHiddenChange}
                />
                <span className={style['description-footer-label']} onClick={this.onHiddenChange}>
                  Invisible to client
                </span>
              </>
            )}
            {['xpert', 'skipper', 'concept'].includes(model.get('type')) ? null : (
              <>
                <Checkbox
                  className="p-checkbox-sm"
                  value="Unkillable"
                  checked={model.get('protected')}
                  onChange={this.onProtectedChange}
                />
                <span className={style['description-footer-label']} onClick={this.onProtectedChange}>
                  Unkillable
                </span>
              </>
            )}
          </div>
        ) : null}
      </div>
    )
  }
}

ReportPopupDescription = ContextSubscriber(
  {
    name: 'report',
    contextTypes: {
      state: PropTypes.instanceOf(Map).isRequired,
      model: PropTypes.instanceOf(Map).isRequired,
      position: PropTypes.number.isRequired
    },
    mapToProps(values) {
      return {
        state: values.state,
        model: values.model,
        position: values.position
      }
    }
  },
  {
    name: 'reportList',
    contextTypes: {
      root: PropTypes.any.isRequired,
      url: PropTypes.string.isRequired,
      state: PropTypes.instanceOf(Map).isRequired,
      model: PropTypes.instanceOf(Map).isRequired,
      actions: PropTypes.object.isRequired,
      features: PropTypes.object,
      currentReportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      virtualReportsIds: PropTypes.array.isRequired,
      timeZone: PropTypes.instanceOf(Map),
      isAdmin: PropTypes.bool,
      isSurveyGuest: PropTypes.bool,
      isSurveyViewer: PropTypes.bool,
      isSurveyCompleted: PropTypes.bool,
      getReportFilters: PropTypes.func
    },
    mapToProps(values) {
      return {
        root: values.root,
        url: values.url,
        listState: values.state,
        listModel: values.model,
        actions: values.actions,
        features: values.features,
        currentReportId: values.currentReportId,
        virtualReportsIds: values.virtualReportsIds,
        timeZone: values.timeZone,
        isAdmin: values.isAdmin,
        isSurveyGuest: values.isSurveyGuest,
        isSurveyViewer: values.isSurveyViewer,
        isSurveyCompleted: values.isSurveyCompleted,
        getReportFilters: values.getReportFilters
      }
    }
  }
)(ReportPopupDescription)

export default ReportPopupDescription
